import { Navigate, Outlet } from 'react-router-dom';
import { AuthError } from './auth-error';
import { AuthCallback } from './auth-callback';
import { ImageContentLayout } from '../layouts';
import { AuthLogin } from './auth-login';
import { AppLoader } from '../components/app-loader';
import { AppError } from './app-error';
import { RouteBoundary } from '../layouts/route-boundary';
import { NoNavRouteBoundary } from '../layouts/no-nav-route-boundary';
import { Portfolio } from './portfolio';
import { ActiveNotice } from './active-notice';
import { Dashboard } from './dashboard';
import { AuthLogout } from './auth-logout';
import { UpdatePropertiesView } from './update-properties';
import { PropertyDetails } from './property-details';
import { Settings } from './settings';
import { MyPreferences, NotificationPreferences } from '../features/settings';
import { IntegratedPropertyDetails } from './integrated-property-details';
import { Analytics } from './analytics';
import { NotFoundPage, UnauthedNotFoundPage } from './not-found';
import { ReportedFindings } from './reported-findings';
import { ViewWorkOrder } from './view-work-order';
import { AppErrorBoundary } from '../components/errors/app-error-boundary';
import { ZendeskChat } from '../components/zendesk/zendesk-chat';
import { ScrollToTop } from '../components';
import { GoogleAnalyticsProvider } from '../providers/google-analytics-provider';
import { MinimumStandards } from './minimum-standards';
import { ViewOnlineServiceReport } from '../features/service-report';
import { ViewOnlineQuote } from '../features/quote';

function RootLayout() {
    return (
        <GoogleAnalyticsProvider>
            <ScrollToTop />
            <ZendeskChat />
            <AppErrorBoundary>
                <Outlet />
            </AppErrorBoundary>
        </GoogleAnalyticsProvider>
    );
}

export const routes = [
    {
        element: <RootLayout />,
        children: [
            {
                path: '/auth',
                children: [
                    {
                        path: 'login',
                        element: (
                            <ImageContentLayout>
                                <AuthLogin />
                            </ImageContentLayout>
                        ),
                    },
                    {
                        path: 'logout',
                        element: (
                            <ImageContentLayout>
                                <AuthLogout />
                            </ImageContentLayout>
                        ),
                    },
                    {
                        path: 'error',
                        element: (
                            <ImageContentLayout>
                                <AuthError />
                            </ImageContentLayout>
                        ),
                    },
                    {
                        path: 'callback',
                        element: <AuthCallback loadingElement={<AppLoader />} />,
                    },
                    {
                        path: '*',
                        element: (
                            <ImageContentLayout>
                                <UnauthedNotFoundPage />
                            </ImageContentLayout>
                        ),
                    },
                    {
                        index: true,
                        element: (
                            <ImageContentLayout>
                                <UnauthedNotFoundPage />
                            </ImageContentLayout>
                        ),
                    },
                ],
            },
            {
                element: <RouteBoundary />,
                children: [
                    { path: '/', element: <Dashboard /> },
                    { path: '/portfolio', element: <Portfolio /> },
                    {
                        path: '/settings',
                        element: <Settings />,
                        children: [
                            { index: true, element: <Navigate to="preferences" /> },
                            { path: 'preferences', element: <MyPreferences /> },
                            { path: 'notification-preferences', element: <NotificationPreferences /> },
                        ],
                    },
                    { path: '/action-required/update', element: <UpdatePropertiesView /> },
                    { path: '/property/:propertyId', element: <PropertyDetails /> },
                    { path: '/property/:propertyId/:page', element: <PropertyDetails /> },
                    {
                        path: '/non-managed-property/:agencyPortfolioPropertyId',
                        element: <IntegratedPropertyDetails />,
                    },
                    {
                        path: '/non-managed-property/:agencyPortfolioPropertyId/:page',
                        element: <IntegratedPropertyDetails />,
                    },
                    { path: '/action-required/access-issues', element: <ActiveNotice /> },
                    { path: '/action-required/reported-findings', element: <ReportedFindings /> },
                    { path: '/analytics', element: <Analytics /> },
                    { path: '/error', element: <AppError /> },
                    { path: '/work-order/:propertyRequestId', element: <ViewWorkOrder /> },
                    { path: '/minimum-standards', element: <MinimumStandards /> },
                    { path: '*', element: <NotFoundPage /> },
                ],
            },
            {
                element: <NoNavRouteBoundary />,
                children: [
                    { path: '/view-service-report/:serviceSheetId', element: <ViewOnlineServiceReport /> },
                    { path: '/view-quote/:quoteId', element: <ViewOnlineQuote /> },
                ],
            },
        ],

    },
];
